import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { type FailCode } from '@packages/models/api';
import { failCodesService } from '@web/services/singletons';
import { useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';

const logger = getLogger('techline');

const getGridStyles = createUseStyles({
    grid: ({
        url,
        zoom,
    }: {
        /** Background image URL. */ url: string;
        /** Zoom offset in pixels. */ zoom: number;
    }) => ({
        backgroundSize: '100% 100%',
        width: `calc(38vw - 56px + ${zoom}px)`,
        height: `calc(32vw + ${zoom}px)`,
        backgroundImage: `url(${url})`,
        borderCollapse: 'collapse',
        marginLeft: '56px',
    }),
    gridRow: {
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
    },
    gridCell: {
        border: '1px solid #000',
        position: 'relative',
        '&:hover': {
            cursor: 'pointer',
            'background-color': 'rgb(69 200 218 / 50%)',
        },
    },
    gridCellSelected: {
        backgroundColor: 'rgb(69 200 218 / 50%)',
        cursor: 'pointer',
    },
    xLabels: ({ zoom: _zoom }: { /** Zoom offset in pixels. */ zoom: number }) => ({
        position: 'absolute',
        left: 'calc(50% - 6px)',
        pointerEvents: 'none',
        top: '-28px',
        fontWeight: 500,
    }),
    yLabels: ({ zoom: _zoom }: { /** Zoom offset in pixels. */ zoom: number }) => ({
        position: 'absolute',
        left: '-20px',
        pointerEvents: 'none',
        top: '15px',
        fontWeight: 500,
    }),
    vLabel: {
        position: 'absolute',
        top: '45px',
        left: '39px',
        fontWeight: 'bold',
        fontSize: '20px',
    },
    secondCodeLabel: {
        position: 'absolute',
        top: '0',
        right: '29%',
        fontWeight: 'bold',
        fontSize: '16px',
    },
    thirdCodeLabel: {
        position: 'absolute',
        transform: 'rotate(90deg)',
        top: '50%',
        left: '-78px',
        fontWeight: 'bold',
        fontSize: '16px',
    },
    gridWrapper: {
        marginTop: '20px',
        maxHeight: 'calc(600px + 22px)',
        minHeight: '622px',
        overflow: 'auto',
        paddingTop: '70px',
        position: 'relative',
    },
});

/**
 * Props for the {@link ElectricalGridSelector} component.
 */
type GridProps = {
    /** The x-axis range label ("A-M" or "N-Z") controlling which grid columns are shown. */
    range: string;

    /** Callback invoked when the user clicks a valid grid cell, passing the resolved fail code. */
    updateFailCode: (arg: FailCode) => void;

    /** The wiring diagram background image URL. */
    url: string;

    /** The zoom offset in pixels applied to the grid dimensions. */
    zoom: number;
};

/**
 * Renders a clickable electrical grid overlay on top of a wiring diagram image.
 * Users click a grid cell to select a fail code location; the component validates the
 * selected cell against the backend and reports the resolved fail code via callback.
 *
 * @param props - {@link GridProps}
 * @returns the electrical grid selector JSX
 */
export function ElectricalGridSelector({ url, updateFailCode, zoom, range }: GridProps) {
    const classes = getGridStyles({ url, zoom });

    const { abortSignalRef } = useAbortController();

    const xLabels = range === 'A-M' ? alphaRange('A', 'M') : alphaRange('N', 'Z');
    const yLabels = alphaRange('A', 'M');

    // State
    const [selectedCell, setSelectedCell] = useState({
        row: '',
        col: '',
    });

    // Handlers

    /**
     * Validate the selected grid cell coordinates and report the resolved fail code.
     *
     * @param row - the row letter of the selected grid cell
     * @param col - the column letter of the selected grid cell
     */
    const handleGridCellClick = useCallback(
        (row: string, col: string) => {
            setSelectedCell({ row, col });

            const failCode = `V${col}${row}`;
            failCodesService
                .fetchIsValidFailCode({
                    failCode: failCode,
                    signal: abortSignalRef.current,
                })
                .then((response) => {
                    if (!response.success && response.aborted) {
                        return;
                    } else if (!response.success) {
                        throw response.data;
                    }
                    const code = response.data.failCodes;
                    if (code.length === 0) {
                        alert(`You've selected a fail code that's not valid, please choose another location nearby.`);
                    } else {
                        updateFailCode(code[0]);
                    }
                })
                .catch((error) => {
                    alert('Error checking validity of failcode: ' + error + '. Please refresh & try again.');
                    logger.error(`Failed to check validity of fail code [${failCode}]`, { failCode, error });
                });
        },
        [abortSignalRef, updateFailCode]
    );

    return (
        <div className={classes.gridWrapper}>
            <table className={classes.grid} id="grid">
                <tbody>
                    {[...Array(10)].map((x, i) => (
                        <tr key={i}>
                            {[...Array(13)].map((y, j) => (
                                <td
                                    className={
                                        yLabels[i] == selectedCell.row && xLabels[j] == selectedCell.col
                                            ? `${classes.gridCell} ${classes.gridCellSelected}`
                                            : classes.gridCell
                                    }
                                    key={j}
                                    // eslint-disable-next-line react/jsx-no-bind -- closure required per-cell in nested .map()
                                    onClick={() => handleGridCellClick(yLabels[i], xLabels[j])}
                                >
                                    {j === 0 && <span className={classes.yLabels}>{yLabels[i]}</span>}

                                    {i === 0 && <span className={classes.xLabels}>{xLabels[j]}</span>}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            <label className={classes.vLabel}>V</label>
            <label className={classes.secondCodeLabel}>2ND FAILURE CODE DIGIT</label>
            <label className={classes.thirdCodeLabel}>3RD FAILURE CODE DIGIT</label>
        </div>
    );
}

/**
 * Generate an array of uppercase letters from start to stop (inclusive).
 *
 * @param start - the first letter in the range
 * @param stop - the last letter in the range
 * @returns an array of single-character strings
 * @example
 * alphaRange('A', 'D') // ['A', 'B', 'C', 'D']
 */
function alphaRange(start: string, stop: string): string[] {
    const result = [];
    for (let idx = start.charCodeAt(0), end = stop.charCodeAt(0); idx <= end; ++idx) {
        result.push(String.fromCharCode(idx));
    }
    return result;
}
